import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, Heart, User, Menu, X, LogOut, Settings, Package } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { NotificationBell } from './NotificationBell';
import { SearchAutocomplete } from './SearchAutocomplete';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

export const Header = () => {
  const { user, logout } = useAuth();
  const { cart, itemCount, updateQuantity, removeFromCart } = useCart();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [cartSheetOpen, setCartSheetOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-white/10" data-testid="main-header">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 gap-4">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <img src="https://customer-assets.emergentagent.com/job_fad4c604-2efd-4142-a35f-93aeb2be7f74/artifacts/hzrybxar_logo2.jpg" alt="Phoenix Card Game" className="w-10 h-10 rounded-lg object-cover" />
            <span className="text-xl font-bold text-white font-outfit hidden sm:block">Phoenix Card Game</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-5 whitespace-nowrap">
            <Link to="/" className="text-gray-300 hover:text-white transition-colors" data-testid="nav-home">
              Accueil
            </Link>
            <Link to="/shop" className="text-gray-300 hover:text-white transition-colors" data-testid="nav-shop">
              Boutique
            </Link>
            <Link to="/shop?game=yugioh" className="text-gray-300 hover:text-white transition-colors" data-testid="nav-yugioh">
              Yu-Gi-Oh!
            </Link>
            <Link to="/shop?game=magic" className="text-gray-300 hover:text-white transition-colors" data-testid="nav-magic">
              Magic
            </Link>
            <Link to="/shop?game=pokemon" className="text-gray-300 hover:text-white transition-colors" data-testid="nav-pokemon">
              Pokémon
            </Link>
            <Link to="/shop?game=accessory" className="text-gray-300 hover:text-white transition-colors" data-testid="nav-accessories">
              Accessoires
            </Link>
          </nav>

          {/* Search Bar with Autocomplete */}
          <div className="hidden lg:block">
            <SearchAutocomplete inputClassName="w-48" />
          </div>

          {/* Right Actions */}
          <div className="flex items-center gap-2">
            {user ? (
              <>
                {/* Notification Bell (Admin only) */}
                {user?.role === 'admin' && <NotificationBell />}

                <Link to="/orders" data-testid="orders-nav-link">
                  <Button variant="ghost" size="icon" className="text-gray-300 hover:text-white hover:bg-white/10">
                    <Package className="w-5 h-5" />
                  </Button>
                </Link>

                <Link to="/favorites" data-testid="favorites-link">
                  <Button variant="ghost" size="icon" className="text-gray-300 hover:text-white hover:bg-white/10">
                    <Heart className="w-5 h-5" />
                  </Button>
                </Link>

                {/* Cart Sheet */}
                <Sheet open={cartSheetOpen} onOpenChange={setCartSheetOpen}>
                  <SheetTrigger asChild>
                    <Button variant="ghost" size="icon" className="text-gray-300 hover:text-white hover:bg-white/10 relative" data-testid="cart-button">
                      <ShoppingCart className="w-5 h-5" />
                      {itemCount > 0 && (
                        <span className="absolute -top-1 -right-1 w-5 h-5 bg-fuchsia-600 rounded-full text-xs flex items-center justify-center text-white">
                          {itemCount}
                        </span>
                      )}
                    </Button>
                  </SheetTrigger>
                  <SheetContent className="bg-[#121212] border-white/10 w-full sm:max-w-md" data-testid="cart-sheet">
                    <SheetHeader>
                      <SheetTitle className="text-white font-outfit">Panier</SheetTitle>
                    </SheetHeader>
                    <div className="mt-6 flex flex-col h-[calc(100vh-200px)]">
                      {cart.items.length === 0 ? (
                        <p className="text-gray-400 text-center py-8">Votre panier est vide</p>
                      ) : (
                        <>
                          <div className="flex-1 overflow-auto space-y-4">
                            {cart.items.map((item) => (
                              <div key={item.product_id} className="flex gap-4 p-3 bg-white/5 rounded-lg" data-testid={`cart-item-${item.product_id}`}>
                                <img src={item.image_url} alt={item.name} className="w-16 h-16 object-cover rounded-lg" />
                                <div className="flex-1 min-w-0">
                                  <h4 className="text-white text-sm font-medium truncate">{item.name}</h4>
                                  <p className="text-fuchsia-400 font-semibold">{item.price.toFixed(2)} €</p>
                                  <div className="flex items-center gap-2 mt-1">
                                    <Button
                                      size="icon"
                                      variant="ghost"
                                      className="h-6 w-6 text-gray-400"
                                      onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                                      data-testid={`cart-decrease-${item.product_id}`}
                                    >
                                      -
                                    </Button>
                                    <span className="text-white text-sm">{item.quantity}</span>
                                    <Button
                                      size="icon"
                                      variant="ghost"
                                      className="h-6 w-6 text-gray-400 disabled:opacity-30"
                                      onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                                      disabled={item.quantity >= (item.stock || 999)}
                                      data-testid={`cart-increase-${item.product_id}`}
                                    >
                                      +
                                    </Button>
                                    <Button
                                      size="icon"
                                      variant="ghost"
                                      className="h-6 w-6 text-red-400 ml-auto"
                                      onClick={() => removeFromCart(item.product_id)}
                                      data-testid={`cart-remove-${item.product_id}`}
                                    >
                                      <X className="w-4 h-4" />
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                          <div className="border-t border-white/10 pt-4 mt-4">
                            <div className="flex justify-between text-lg mb-4">
                              <span className="text-gray-300">Total</span>
                              <span className="text-white font-bold">{cart.total.toFixed(2)} €</span>
                            </div>
                            <Button
                              className="w-full bg-fuchsia-600 hover:bg-fuchsia-500 text-white glow-primary"
                              onClick={() => { setCartSheetOpen(false); navigate('/checkout'); }}
                              data-testid="checkout-button"
                            >
                              Passer la commande
                            </Button>
                          </div>
                        </>
                      )}
                    </div>
                  </SheetContent>
                </Sheet>

                {/* User Dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="text-gray-300 hover:text-white hover:bg-white/10" data-testid="user-menu-button">
                      {user.picture ? (
                        <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
                      ) : (
                        <User className="w-5 h-5" />
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="bg-[#1a1a1a] border-white/10" align="end">
                    <div className="px-3 py-2">
                      <p className="text-white font-medium">{user.name}</p>
                      <p className="text-gray-400 text-sm">{user.email}</p>
                    </div>
                    <DropdownMenuSeparator className="bg-white/10" />
                    <DropdownMenuItem asChild className="text-gray-300 hover:text-white focus:bg-white/10 cursor-pointer">
                      <Link to="/orders" data-testid="orders-link">
                        <Package className="w-4 h-4 mr-2" />
                        Vos Commandes
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild className="text-gray-300 hover:text-white focus:bg-white/10 cursor-pointer">
                      <Link to="/favorites">
                        <Heart className="w-4 h-4 mr-2" />
                        Favoris
                      </Link>
                    </DropdownMenuItem>
                    {user?.role === 'admin' && (
                      <DropdownMenuItem asChild className="text-gray-300 hover:text-white focus:bg-white/10 cursor-pointer">
                        <Link to="/admin" data-testid="admin-link">
                          <Settings className="w-4 h-4 mr-2" />
                          Admin
                        </Link>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator className="bg-white/10" />
                    <DropdownMenuItem onClick={handleLogout} className="text-red-400 hover:text-red-300 focus:bg-white/10 cursor-pointer" data-testid="logout-button">
                      <LogOut className="w-4 h-4 mr-2" />
                      Déconnexion
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <div className="flex items-center gap-2">
                <Link to="/login">
                  <Button variant="ghost" className="text-gray-300 hover:text-white" data-testid="login-button">
                    Connexion
                  </Button>
                </Link>
                <Link to="/register">
                  <Button className="bg-fuchsia-600 hover:bg-fuchsia-500 text-white" data-testid="register-button">
                    Inscription
                  </Button>
                </Link>
              </div>
            )}

            {/* Social Links */}
            <div className="hidden sm:flex items-center gap-1 ml-2 pl-2 border-l border-white/10">
              <a href="https://discord.gg/nXUF7yAvQd" target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-full flex items-center justify-center text-[#5865F2] hover:bg-[#5865F2]/15 transition-colors" data-testid="social-discord" title="Discord">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/></svg>
              </a>
              <a href="https://twitch.tv/phoenixcardgame" target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-full flex items-center justify-center text-[#9146FF] hover:bg-[#9146FF]/15 transition-colors" data-testid="social-twitch" title="Twitch">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714z"/></svg>
              </a>
              <a href="https://tiktok.com/@phoenixcardgame" target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-full flex items-center justify-center text-white hover:bg-white/10 transition-colors" data-testid="social-tiktok" title="TikTok">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 0 0-.79-.05A6.34 6.34 0 0 0 3.15 15.2a6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.34-6.34V8.73a8.19 8.19 0 0 0 4.76 1.52v-3.4a4.85 4.85 0 0 1-1-.16z"/></svg>
              </a>
              <a href="https://youtube.com/@PhoenixCardGame" target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-full flex items-center justify-center text-[#FF0000] hover:bg-[#FF0000]/15 transition-colors" data-testid="social-youtube" title="YouTube">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
              </a>
              <a href="https://www.instagram.com/phoenixcardgametcg/" target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-full flex items-center justify-center text-[#E4405F] hover:bg-[#E4405F]/15 transition-colors" data-testid="social-instagram" title="Instagram">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
              </a>
            </div>

            {/* Mobile Menu Toggle */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden text-gray-300"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-white/10">
            <nav className="flex flex-col gap-4">
              <Link to="/" className="text-gray-300 hover:text-white" onClick={() => setMobileMenuOpen(false)}>
                Accueil
              </Link>
              <Link to="/shop" className="text-gray-300 hover:text-white" onClick={() => setMobileMenuOpen(false)}>
                Boutique
              </Link>
              <Link to="/shop?game=yugioh" className="text-gray-300 hover:text-white" onClick={() => setMobileMenuOpen(false)}>
                Yu-Gi-Oh!
              </Link>
              <Link to="/shop?game=magic" className="text-gray-300 hover:text-white" onClick={() => setMobileMenuOpen(false)}>
                Magic
              </Link>
              <Link to="/shop?game=pokemon" className="text-gray-300 hover:text-white" onClick={() => setMobileMenuOpen(false)}>
                Pokémon
              </Link>
            </nav>
            <div className="mt-4">
              <SearchAutocomplete inputClassName="w-full" onNavigate={() => setMobileMenuOpen(false)} />
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

